import React, {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import DateUtils, {DateUtilsTypes} from "../../../../../../../../core/services/date-utils";
import useSearch, {SearchDataSettingsOrderBy, SearchDataSettingsPaginationInfo, UseSearchFunction} from "../../../../../../../hooks/use-search";
import {
    DashboardEntry,
    DashboardFilterParameter,
    DashboardViewFilterSectionFilters,
    DashboardWidgetData,
    DashboardWidgetEntry,
    DashboardWidgetType,
    SavedUiComponentStateWidgetConfig
} from "../../types";
import {CancelToken, CancelTokenSource} from 'axios';
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import useIndustry from '../../../../../../../hooks/use-industry';
import Api from "../../../../../../../../core/services/api/api";
import {deepEqual, exportCSVFile} from "../../../../../../../../core/services/utils/utils";
import {useUpdateEffect} from "react-use";
import {DashboardWidgetConfig} from "../../dashboard-widget.config";
import {DateRangeUtils} from "../../../../../../../components/app-specific/date-range-controller";
import {
    generateBusinessName,
    generateCategoryName,
    generatePartNumber,
    generatePersonName,
    generateReasonCode,
    generateReasonTitle,
    generateTerritoryName,
    generateVendorCode
} from "../../../../../../../../core/services/utils/name-generator";
import EnvService from "../../../../../../../../core/services/env";


interface DashboardWidgetCache {
    data: DashboardWidgetData;
    timestamp: number;
    hashCode: string; // Unique identifier for the cache entry
    filtersHashCode?: string;
}

interface UseWidgetDataProps {
    widgetId: DashboardWidgetEntry['id'];
    widgetConnectionId?: DashboardWidgetEntry['connectionId'];
    widgetType?: DashboardWidgetType;
    widgetVariant?: string;
    widgetTitle: DashboardWidgetEntry['title'];
    dashboardId?: DashboardEntry['id'];
    parameters: DashboardWidgetEntry['parameters'];
    filters: DashboardViewFilterSectionFilters;
    cacheTimeout?: number;
    isEnabled: boolean;
    initialPagination?: SearchDataSettingsPaginationInfo;
    widgetOrderBy?: SearchDataSettingsOrderBy;
    usePagination?: boolean;
    useSorting?: boolean;
    saveWidgetOrderBy?: (data: SavedUiComponentStateWidgetConfig) => Promise<void>;
}

interface UseWidgetDataReturn {
    data: DashboardWidgetData | null;
    isLoading: boolean;
    hasFetchError: boolean;
    refresh: () => Promise<void>;
    lastFetched: DateUtilsTypes.Instance | null;
    paginationInfo?: SearchDataSettingsPaginationInfo;
    setPaginationInfo: React.Dispatch<React.SetStateAction<SearchDataSettingsPaginationInfo | undefined>>;
    orderBy?: SearchDataSettingsOrderBy;
    setOrderBy?: React.Dispatch<React.SetStateAction<SearchDataSettingsOrderBy | undefined>>;
    exportData?: () => Promise<void>;
    isExporting?: boolean;
}

function getHash(value: any): string {
    if (value === null || value === undefined) {
        return 'null';
    }
    if (Array.isArray(value)) {
        // Unordered: stringify each element, sort, then join
        const parts = value.map(el => getHash(el));
        parts.sort();
        return `[${parts.join(',')}]`;
    }
    if (typeof value === 'object') {
        // Sort object keys to ensure deterministic order
        const keys = Object.keys(value).sort();
        const parts = keys.map(key => `${key}:${getHash(value[key])}`);
        return `{${parts.join(',')}}`;
    }
    // Primitives
    return JSON.stringify(value);
}

/**
 * Hook to manage widget data fetching and caching
 */
export const useDashboardWidgetData = ({
                                           widgetId,
                                           widgetConnectionId,
                                           widgetType,
                                           widgetVariant,
                                           widgetTitle,
                                           dashboardId,
                                           parameters,
                                           filters,
                                           cacheTimeout = 5 * 60 * 1000, // 5 minutes default
                                           isEnabled,
                                           initialPagination,
                                           widgetOrderBy,
                                           usePagination = false,
                                           useSorting = false,
                                           saveWidgetOrderBy,
                                       }: UseWidgetDataProps): UseWidgetDataReturn => {
    const {cachedIndustry, industries} = useIndustry();
    const [data, setData] = useState<DashboardWidgetData | null>(null);
    const [hasFetchError, setHasFetchError] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [lastFetched, setLastFetched] = useState<DateUtilsTypes.Instance | null>(null);
    const isMounted = useIsMounted();
    const cacheRef = useRef<DashboardWidgetCache[]>([]);

    const params = useMemo(() => parameters?.map((param) => param.name) ?? [], [parameters]);

    const orderByOrDefault = useMemo(() => {
        if (!!widgetOrderBy?.field && useSorting) {
            return widgetOrderBy;
        }
        return {
            field: new DashboardWidgetConfig(widgetId, widgetVariant, widgetType).defaultOrderByField?.name ?? "",
            descending: true,
        }
    }, [widgetOrderBy, widgetId, widgetType, widgetVariant, useSorting]);

    /**
     * Check if the cache is valid
     */
    const getCache = useCallback((
        filters?: DashboardViewFilterSectionFilters,
        paginationInfo?: { currentPage?: number, pageSize?: number },
        orderBy?: SearchDataSettingsOrderBy,
    ): DashboardWidgetCache | undefined => {
        const cache = cacheRef.current;
        if (!cache || cache.length === 0)
            return;
        const filtersHashCode = getHash({filters: filters});
        const cachedEntries = cache.filter(e => filtersHashCode === e.filtersHashCode);
        if (!cachedEntries.length) {
            return;
        }
        const now = DateUtils.localNow().valueOf();
        const isExpired = cachedEntries.every(e => (now - e.timestamp) > cacheTimeout);
        if (isExpired) {
            return;
        }
        const hashCode = getHash({filters: filters, paginationInfo: paginationInfo, orderBy: orderBy});
        const cachedEntry = cachedEntries.find(e => e.hashCode === hashCode);
        if (!cachedEntry) {
            return;
        }
        return cachedEntry;
    }, [cacheTimeout]);

    /**
     * Prepare filters for the API request
     */
    const prepareFilters = useCallback((filters?: DashboardViewFilterSectionFilters): Record<string, any> => {
        filters = filters ?? {};
        const result = {}
        if (params.includes(DashboardFilterParameter.CurrencyCode)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.CurrencyCode)!;
            result[parameter.name] = filters.currency;
        }
        if (params.includes(DashboardFilterParameter.StartDate) || params.includes(DashboardFilterParameter.EndDate)) {
            const startDateParameter = parameters.find((param) => param.name === DashboardFilterParameter.StartDate);
            const endDateParameter = parameters.find((param) => param.name === DashboardFilterParameter.EndDate);

            const range = {
                startDate: undefined as string | undefined,
                endDate: undefined as string | undefined,
            }

            const dateRangeValue = DateRangeUtils.getDateRangeValueFromLegacyFilters(filters);
            if (dateRangeValue) {
                const determinedRange = DateRangeUtils.getDateRange(dateRangeValue);
                if (determinedRange) {
                    const [determinedStart, determinedEnd] = determinedRange;
                    range.startDate = determinedStart ? DateUtils.localToServer(determinedStart).format(DateUtils.withoutOffsetFormat) : determinedStart;
                    range.endDate = determinedEnd ? DateUtils.localToServer(determinedEnd).format(DateUtils.withoutOffsetFormat) : determinedEnd;
                }
            } else {
                const dateRangeFromFlag = DateRangeUtils.toDateRangeFromDateRangeFlag((filters as any).dateRangeFlag as string | undefined);
                if (dateRangeFromFlag) {
                    const determinedRange = DateRangeUtils.getDateRange(dateRangeFromFlag);
                    if (determinedRange) {
                        const [determinedStart, determinedEnd] = determinedRange;
                        range.startDate = determinedStart ? DateUtils.localToServer(determinedStart).format(DateUtils.withoutOffsetFormat) : determinedStart;
                        range.endDate = determinedEnd ? DateUtils.localToServer(determinedEnd).format(DateUtils.withoutOffsetFormat) : determinedEnd;
                    }
                }
            }
            result[startDateParameter?.name ?? "StartDate"] = range.startDate;
            result[endDateParameter?.name ?? "EndDate"] = range.endDate;
        }
        if (params.includes(DashboardFilterParameter.Industry)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.Industry)!;
            result[parameter.name] = filters.industry;
        }
        if (params.includes(DashboardFilterParameter.ExcludePartNumbers)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.ExcludePartNumbers)!;
            result[parameter.name] = filters.excludePartNumbers;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => {
                    try {
                        return JSON.parse(e).partNo;
                    } catch {
                        return e;
                    }
                }));
            }
        }
        if (params.includes(DashboardFilterParameter.ExcludeCustomerIds)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.ExcludeCustomerIds)!;
            result[parameter.name] = filters.excludeCustomers;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).internalId));
            }
        }
        if (params.includes(DashboardFilterParameter.PartNumbers)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.PartNumbers)!;
            result[parameter.name] = filters.partNumbers;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).partNo));
            }
        }
        if (params.includes(DashboardFilterParameter.PartNumberPrefix)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.PartNumberPrefix)!;
            result[parameter.name] = filters.partNumberPrefix;
        }
        if (params.includes(DashboardFilterParameter.CustomerIds)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.CustomerIds)!;
            result[parameter.name] = filters.customers;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).internalId));
            }
        }
        if (params.includes(DashboardFilterParameter.PartCategories)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.PartCategories)!;
            result[parameter.name] = filters.categories;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).id));
            }
        }
        if (params.includes(DashboardFilterParameter.TerritoryCodes)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.TerritoryCodes)!;
            result[parameter.name] = filters.territoryCodes;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name]);
            }
        }
        if (params.includes(DashboardFilterParameter.PSR_ReasonIds)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.PSR_ReasonIds)!;
            result[parameter.name] = filters.partSupportReasons;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).id));
            }
        }
        if (params.includes(DashboardFilterParameter.PSR_StatusIds)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.PSR_StatusIds)!;
            result[parameter.name] = filters.partSupportStatuses;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).id));
            }
        }
        if (params.includes(DashboardFilterParameter.PSR_Types)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.PSR_Types)!;
            result[parameter.name] = filters.partSupportTypes;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).id));
            }
        }
        if (params.includes(DashboardFilterParameter.PSR_VendorStatus)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.PSR_VendorStatus)!;
            result[parameter.name] = filters.partSupportVendorStatuses;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).id));
            }
        }
        if (params.includes(DashboardFilterParameter.WarehouseCodes)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.WarehouseCodes)!;
            result[parameter.name] = filters.warehouses;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).code));
            }
        }
        if (params.includes(DashboardFilterParameter.SalesWarehouseCodes)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.SalesWarehouseCodes)!;
            result[parameter.name] = filters.salesWarehouses;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).code));
            }
        }
        if (params.includes(DashboardFilterParameter.OperatorIds)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.OperatorIds)!;
            result[parameter.name] = filters.operators;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).id));
            }
        }
        if (params.includes(DashboardFilterParameter.PSR_ExcludeReasonIds)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.PSR_ExcludeReasonIds)!;
            result[parameter.name] = filters.partSupportExcludeReasons;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).id));
            }
        }
        if (params.includes(DashboardFilterParameter.HasCredited)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.HasCredited)!;
            result[parameter.name] = filters.hasCredited;
        }
        if (params.includes(DashboardFilterParameter.ProvinceCodes)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.ProvinceCodes)!;
            result[parameter.name] = filters.provinces;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).code));
            }
        }
        if (params.includes(DashboardFilterParameter.Cities)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.Cities)!;
            result[parameter.name] = filters.cities;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name]);
            }
        }
        if (params.includes(DashboardFilterParameter.AccountManagersCodes)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.AccountManagersCodes)!;
            result[parameter.name] = filters.accountManagers;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).code));
            }
        }
        if (params.includes(DashboardFilterParameter.ArTransactionTypes)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.ArTransactionTypes)!;
            result[parameter.name] = filters.arTransactionTypes;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name].map(e => JSON.parse(e).id));
            }
        }
        if (params.includes(DashboardFilterParameter.VendorNumbers)) {
            const parameter = parameters.find((param) => param.name === DashboardFilterParameter.VendorNumbers)!;
            result[parameter.name] = filters.vendorNumbers;
            if (Array.isArray(result[parameter.name]) && result[parameter.name].length === 0) {
                result[parameter.name] = undefined;
            }
            if (!!result[parameter.name]) {
                result[parameter.name] = JSON.stringify(result[parameter.name]);
            }
        }
        // TODO: add more filters
        return result;
    }, [parameters, params])

    /**
     * Fetch data for the dashboard widget.
     *
     * If null is returned, the fetch failed.
     * If undefined is returned, the fetch was aborted or no valid sorting field was provided.
     * If data is returned, the fetch was successful.
     */
    const getDataForDashboardWidget = useCallback(async (
        filters: Record<string, any>,
        {
            pagination,
            orderBy,
            cancelToken,
            forExport,
        }: {
            pagination?: { currentPage: number, pageSize: number },
            orderBy?: SearchDataSettingsOrderBy,
            cancelToken?: CancelToken,
            forExport?: boolean,
        }
    ): Promise<DashboardWidgetData | undefined | null> => {
        const forApi = {
            widgetId: widgetId,
            dashboardId: dashboardId,
            pageNumber: pagination?.currentPage ?? 1,
            parameters: filters,
            isDesc: orderBy?.descending ?? true,
            sortByField: orderBy?.field,
            loadAllData: !usePagination ? true : forExport ?? false,
        };
        if (forApi.sortByField === "") {
            // No valid sorting field therefore abort.
            return undefined;
        }
        const response = await Api.loadCustomDashboardWidgetData(forApi, cancelToken);
        if (!isMounted())
            return undefined;
        if (response?.aborted)
            return undefined;
        if (!response?.resultFlag)
            return null;
        try {
            return {
                data: JSON.parse(response.data?.data as string),
            }
        } catch (e) {
            return null
        }
    }, [widgetId, dashboardId, usePagination, isMounted]);

    /**
     * Search function for the useSearch hook
     */
    const searchFunction = useCallback<UseSearchFunction>(async ({filters, paginationInfo, orderBy, cancelTokenSource, setLoading}) => {
        if (!isEnabled)
            return;
        let newData: DashboardWidgetData | undefined | null;
        let fromCache = false;
        const preppedFilters = prepareFilters(filters);
        const cache = getCache(preppedFilters, paginationInfo, orderBy);
        if (!cache) {
            newData = await getDataForDashboardWidget(preppedFilters, {
                pagination: usePagination ? paginationInfo : undefined,
                orderBy: orderBy,
                cancelToken: cancelTokenSource?.token
            });
        } else {
            fromCache = true;
            newData = cache.data ?? undefined;
        }
        if (newData?.data && EnvService.enableDemoNameMasking) {
            newData.data = newData.data.map(e => ({
                ...e,
                ...(e.TerritoryCode && {
                    TerritoryCode: generateTerritoryName(e.TerritoryCode),
                }),
                ...(e.Customer_Name && {
                    Customer_Name: generateBusinessName(e.Customer_Name),
                }),
                ...(e.CustomerName && {
                    CustomerName: generateBusinessName(e.CustomerName),
                }),
                ...(e.VendorCode && {
                    VendorCode: generateVendorCode(e.VendorCode),
                }),
                ...(e.Vendor_Code && {
                    Vendor_Code: generateVendorCode(e.Vendor_Code),
                }),
                ...(e.Vendor_Number && {
                    Vendor_Number: generateVendorCode(e.Vendor_Number),
                }),
                ...(e.Vendor_Name && {
                    Vendor_Name: generateBusinessName(e.Vendor_Name),
                }),
                ...(e.Operator_Firstname && {
                    Operator_Firstname: generatePersonName(e.Operator_Firstname),
                }),
                ...(e.Operator_Lastname && {
                    Operator_Lastname: generatePersonName(e.Operator_Lastname),
                }),
                ...(e.PartNumber && {
                    PartNumber: generatePartNumber(e.PartNumber),
                }),
                ...(e.PartNo && {
                    PartNo: generatePartNumber(e.PartNo),
                }),
                ...(e.Category && {
                    Category: generateCategoryName(e.Category),
                }),
                ...(e.Reason_Code && {
                    Reason_Code: generateReasonCode(e.Reason_Code),
                }),
                ...(e.ReasonCode && {
                    ReasonCode: generateReasonCode(e.ReasonCode),
                }),
                ...(e.Reason_Title && {
                    Reason_Title: generateReasonTitle(e.Reason_Title),
                }),
                ...(e.Status && {
                    Status: generateReasonTitle(e.Status),
                }),
            }))
        }
        if (!isMounted() || typeof newData === 'undefined')
            return;
        setLoading?.(false);
        if (newData === null) {
            setHasFetchError(true);
            setData({data: []});
        } else {
            setHasFetchError(false);
            setData(newData);
        }
        if (!fromCache && !!newData) {
            setLastFetched(DateUtils.localNow());
            cacheRef.current = [
                ...(cacheRef.current ?? []),
                {
                    data: newData,
                    timestamp: DateUtils.localNow().valueOf(),
                    hashCode: getHash({filters: preppedFilters, paginationInfo: paginationInfo, orderBy: orderBy}),
                    filtersHashCode: getHash({filters: preppedFilters}),
                } satisfies DashboardWidgetCache,
            ]
        }
    }, [getDataForDashboardWidget, getCache, isEnabled, isMounted, prepareFilters, usePagination]);

    /**
     * Search settings for the useSearch hook
     */
    const searchSettings = useMemo(() => ({
        paginationInfo: initialPagination,
        orderBy: orderByOrDefault,
        disabled: !isEnabled,
        filters: {...filters},
    }), [initialPagination, orderByOrDefault, isEnabled, filters]);

    // Initialize search hook for pagination and sorting
    const {paginationInfo, setPaginationInfo, orderBy, setOrderBy, loading: isLoading, performSearch: _performSearch, setFilters} = useSearch(searchFunction, searchSettings, false, 700);

    /**
     * Set the order by value and save it to the widget state
     */
    const setOrderByAugmented = useCallback<Dispatch<SetStateAction<SearchDataSettingsOrderBy | undefined>>>((orderBy) => {
        setOrderBy?.(prevState => {
            const newState = typeof orderBy === 'function' ? orderBy(prevState) : orderBy;
            if (!deepEqual(prevState, newState) && !!widgetConnectionId) {
                void saveWidgetOrderBy?.({connectionId: widgetConnectionId, orderBy: newState})
            }
            return newState;
        })
    }, [saveWidgetOrderBy, setOrderBy, widgetConnectionId]);

    const cachedIndustryId = (cachedIndustry as any)?.id;
    /**
     * Update filters and perform search
     */
    useEffect(() => {
        if (!isEnabled) return;
        const newFilters = {...filters};
        if (params.includes(DashboardFilterParameter.Industry) || params.includes(DashboardFilterParameter.PartCategories)) {
            newFilters.industry = filters.industry ?? cachedIndustryId;
        }
        setFilters?.(newFilters);
    }, [filters, isEnabled, setFilters, cachedIndustryId, params]);

    /**
     * Update the order by value when the initial order by changes
     */
    useUpdateEffect(() => {
        if (!isEnabled) return;
        if (deepEqual(orderByOrDefault, orderBy)) {
            return;
        }
        setOrderBy?.(orderByOrDefault);
    }, [orderByOrDefault, setOrderBy, isEnabled]);

    /**
     * Perform search function with cache check
     */
    const performSearch = useCallback(async (cancelTokenSource?: CancelTokenSource) => {
        const cache = getCache(prepareFilters(filters), {currentPage: paginationInfo?.currentPage, pageSize: paginationInfo?.pageSize}, orderBy);
        if (!!cache) {
            setHasFetchError(false);
            setData(cache!.data);
            return;
        }
        await _performSearch(cancelTokenSource);
    }, [_performSearch, filters, getCache, orderBy, paginationInfo?.currentPage, paginationInfo?.pageSize, prepareFilters])

    /**
     * Refresh widget data.
     *
     * Removes the cache entry for the current filters and pagination,
     * then performs a search to fetch fresh data.
     */
    const refresh = useCallback(async () => {
        if (!isEnabled) {
            return;
        }
        const filtersHashCode = getHash({filters: prepareFilters(filters)});
        cacheRef.current = cacheRef.current.filter(e => e.filtersHashCode !== filtersHashCode);
        await performSearch();
    }, [isEnabled, prepareFilters, filters, performSearch]);

    /**
     * Export data for the dashboard widget
     */
    const exportData = useCallback(async () => {
        setIsExporting(true);
        const data = await getDataForDashboardWidget(prepareFilters(filters), {
            forExport: true,
            pagination: usePagination ? paginationInfo : undefined,
            orderBy: orderByOrDefault,
        });
        if (!isMounted())
            return;
        setIsExporting(false);
        if (!data?.data)
            return;
        const entries = data.data;
        let title = widgetTitle;
        // number of applied filters and types.
        const map = {
            industry: DashboardFilterParameter.Industry,
            currency: DashboardFilterParameter.CurrencyCode,
            dateRangeValue: DashboardFilterParameter.StartDate,
            excludePartNumbers: DashboardFilterParameter.ExcludePartNumbers,
            excludeCustomers: DashboardFilterParameter.ExcludeCustomerIds,
            partNumbers: DashboardFilterParameter.PartNumbers,
            partNumberPrefix: DashboardFilterParameter.PartNumberPrefix,
            customers: DashboardFilterParameter.CustomerIds,
            categories: DashboardFilterParameter.PartCategories,
            territoryCodes: DashboardFilterParameter.TerritoryCodes,
            partSupportReasons: DashboardFilterParameter.PSR_ReasonIds,
            partSupportStatuses: DashboardFilterParameter.PSR_StatusIds,
            partSupportTypes: DashboardFilterParameter.PSR_Types,
            partSupportVendorStatuses: DashboardFilterParameter.PSR_VendorStatus,
            warehouses: DashboardFilterParameter.WarehouseCodes,
            salesWarehouses: DashboardFilterParameter.SalesWarehouseCodes,
            operators: DashboardFilterParameter.OperatorIds,
            partSupportExcludeReasons: DashboardFilterParameter.PSR_ExcludeReasonIds,
            hasCredited: DashboardFilterParameter.HasCredited,
            provinces: DashboardFilterParameter.ProvinceCodes,
            cities: DashboardFilterParameter.Cities,
            accountManagers: DashboardFilterParameter.AccountManagersCodes,
            arTransactionTypes: DashboardFilterParameter.ArTransactionTypes,
            vendorNumbers: DashboardFilterParameter.VendorNumbers,
            // TODO: add more filters
        }
        const appliedFilters = Object.keys(filters).filter((key) => filters[key] !== undefined && filters[key] !== null && filters[key] !== '').map((key) => map[key as keyof typeof map]).filter(e => !!e);
        const appliedFiltersCount = appliedFilters.length;

        const appliedFiltersTypes = appliedFilters.map((key) => {
            if (key === DashboardFilterParameter.StartDate) {
                const dateRangeValue = DateRangeUtils.getDateRangeValueFromLegacyFilters(filters);
                if (dateRangeValue) {
                    const determinedRange = DateRangeUtils.displayDateRange(dateRangeValue);
                    if (determinedRange) {
                        return `Date: ${determinedRange}`;
                    }
                }
                const range = {
                    startDate: (filters as any).startDate ? DateUtils.server((filters as any).startDate) : undefined,
                    endDate: (filters as any).endDate ? DateUtils.server((filters as any).endDate) : undefined,
                }
                let res = 'Date';
                // now append range:
                if (range.startDate) {
                    const startDate = range.startDate?.format(DateUtils.customFormats.dashboardWidgetDate);
                    res += ` from ${startDate}`;
                }
                if (range.endDate) {
                    const endDate = range.endDate?.format(DateUtils.customFormats.dashboardWidgetDate);
                    res += ` to ${endDate}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.Industry) {
                const industry = industries.find((industry: any) => industry.id === filters.industry);
                let res = 'Industry';
                if (industry) {
                    res += `: ${(industry as any).title})`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.CurrencyCode) {
                let res = 'Currency';
                if (filters.currency) {
                    res += `: ${filters.currency}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.ExcludePartNumbers) {
                let res = 'Exclude Part Numbers';
                if (filters.excludePartNumbers && filters.excludePartNumbers.length > 0) {
                    res += `: ${filters.excludePartNumbers.map(e => {
                        try {
                            return JSON.parse(e).partNo;
                        } catch {
                            return e;
                        }
                    }).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.ExcludeCustomerIds) {
                let res = 'Exclude Customers';
                if (filters.excludeCustomers && filters.excludeCustomers.length > 0) {
                    res += `: ${filters.excludeCustomers.map(e => JSON.parse(e).name).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.PartNumbers) {
                let res = 'Part Numbers';
                if (filters.partNumbers && filters.partNumbers.length > 0) {
                    res += `: ${filters.partNumbers.join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.PartNumberPrefix) {
                let res = 'Part Number Prefix';
                if (filters.partNumberPrefix && filters.partNumberPrefix?.length > 0) {
                    res += `: ${filters.partNumberPrefix}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.CustomerIds) {
                let res = 'Customers';
                if (filters.customers && filters.customers.length > 0) {
                    res += `: ${filters.customers.map(e => JSON.parse(e).name).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.PartCategories) {
                let res = 'Categories';
                if (filters.categories && filters.categories.length > 0) {
                    res += `: ${filters.categories.map(e => JSON.parse(e).title).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.TerritoryCodes) {
                let res = 'Territories';
                if (filters.territoryCodes && filters.territoryCodes.length > 0) {
                    res += `: ${filters.territoryCodes.join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.PSR_ReasonIds) {
                let res = 'Part Support Reasons';
                if (filters.partSupportReasons && filters.partSupportReasons.length > 0) {
                    res += `: ${filters.partSupportReasons.map(e => JSON.parse(e).code).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.PSR_StatusIds) {
                let res = 'Part Support Statuses';
                if (filters.partSupportStatuses && filters.partSupportStatuses.length > 0) {
                    res += `: ${filters.partSupportStatuses.map(e => JSON.parse(e).title).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.PSR_Types) {
                let res = 'Part Support Types';
                if (filters.partSupportTypes && filters.partSupportTypes.length > 0) {
                    res += `: ${filters.partSupportTypes.map(e => JSON.parse(e).title).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.PSR_VendorStatus) {
                let res = 'Part Support Vendor Statuses';
                if (filters.partSupportVendorStatuses && filters.partSupportVendorStatuses.length > 0) {
                    res += `: ${filters.partSupportVendorStatuses.map(e => JSON.parse(e).title).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.WarehouseCodes) {
                let res = 'Warehouses';
                if (filters.warehouses && filters.warehouses.length > 0) {
                    res += `: ${filters.warehouses.map(e => JSON.parse(e).code).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.SalesWarehouseCodes) {
                let res = 'Sales Warehouses';
                if (filters.salesWarehouses && filters.salesWarehouses.length > 0) {
                    res += `: ${filters.salesWarehouses.map(e => JSON.parse(e).code).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.OperatorIds) {
                let res = 'Operators';
                if (filters.operators && filters.operators.length > 0) {
                    res += `: ${filters.operators.map(e => {
                        const parsed = JSON.parse(e)
                        return [parsed.firstName, parsed.lastName].filter(Boolean).join(' ');
                    }).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.PSR_ExcludeReasonIds) {
                let res = 'Part Support Exclude Reasons';
                if (filters.partSupportExcludeReasons && filters.partSupportExcludeReasons.length > 0) {
                    res += `: ${filters.partSupportExcludeReasons.map(e => JSON.parse(e).code).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.HasCredited) {
                let res = 'Has Credited';
                if (typeof filters.hasCredited === 'boolean') {
                    res += `: ${filters.hasCredited ? 'Yes' : 'No'}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.ProvinceCodes) {
                let res = 'Provinces';
                if (filters.provinces && filters.provinces.length > 0) {
                    res += `: ${filters.provinces.map(e => JSON.parse(e).name).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.Cities) {
                let res = 'Cities';
                if (filters.cities && filters.cities.length > 0) {
                    res += `: ${filters.cities.join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.AccountManagersCodes) {
                let res = 'Account Managers';
                if (filters.accountManagers && filters.accountManagers.length > 0) {
                    res += `: ${filters.accountManagers.map(e => JSON.parse(e).code).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.ArTransactionTypes) {
                let res = 'AR Transaction Types';
                if (filters.arTransactionTypes && filters.arTransactionTypes.length > 0) {
                    res += `: ${filters.arTransactionTypes.map(e => JSON.parse(e).title).join(', ')}`;
                }
                return res;
            }
            if (key === DashboardFilterParameter.VendorNumbers) {
                let res = 'Vendor Numbers';
                if (filters.vendorNumbers && filters.vendorNumbers.length > 0) {
                    res += `: ${filters.vendorNumbers.join(', ')}`;
                }
                return res;
            }
            // TODO: add more filters
            return key;
        });
        if (appliedFiltersCount > 0) {
            title += ` (${appliedFiltersCount} ${appliedFiltersCount > 1 ? 'filters' : 'filter'})`;
        }
        if (appliedFiltersCount > 0) {
            title += ` - ${appliedFiltersTypes.join(', ')}`;
        }
        exportCSVFile(entries, title);
    }, [filters, getDataForDashboardWidget, industries, isMounted, orderByOrDefault, paginationInfo, prepareFilters, usePagination, widgetTitle]);

    /**
     * With each change in the exporting value of the state:
     * - opens or closes the exporting dialog of the application.
     */
    useEffect(() => {
        if (isExporting) {
            window.partner?.openExportingDialog?.();
        } else {
            window.partner?.closeExportingDialog?.();
        }
    }, [isExporting])


    return {
        data,
        isLoading: isLoading && isEnabled,
        hasFetchError,
        refresh,
        lastFetched,
        paginationInfo,
        setPaginationInfo,
        orderBy: orderByOrDefault,
        setOrderBy: setOrderByAugmented,
        isExporting,
        exportData,
    };
};

export default useDashboardWidgetData;